// apps/client/src/assets/builders/house.ts — revision 10 (D-04, owner decision): KayKit Medieval Hexagon cottages, as in the
// approved look demo. Level 0 is the Kenney tent; levels 1–4 compose `building_home_A|B_<roof>` pieces from the `w.house` asset
// entry's `parts`. Only the layout lives here; the models (and their window panes) come from the asset map, so they can be swapped.
// M1-T14 adds two small code-built overlays inside the 3 × 3 footprint: the construction scaffold while `building`, and on a resident's
// house (`resident`) a white picket fence and a flower bed in the owner's friend dye (`friendDye`) in the front garden strip.
import { ROOF_COLORS, type HouseLevel, type RoofColor } from '@whiskerton/content';
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { toyMaterial } from '../../characters/doll';
import type { CodeBuilder } from '../service';

export interface HousePart { name: 'tent' | 'main' | 'backRoom' | 'sideRoom'; part: string; fit: number; x: number; z: number; facing: 0 | 1 | 2 | 3 }
export const GARDEN_Z = 1.0;   // pieces stay at z ≤ 1.0; the front 0.5 of the 3 × 3 footprint is the garden strip (M1-T14)
export function roofOf(v: unknown): RoofColor { return (ROOF_COLORS as readonly unknown[]).includes(v) ? (v as RoofColor) : 'red'; }
export function houseParts(level: HouseLevel, roof: RoofColor): HousePart[] {
  const A = `home_A_${roof}`, B = `home_B_${roof}`;
  const back: HousePart = { name: 'backRoom', part: A, fit: 1.4, x: -0.75, z: -0.8, facing: 0 };
  switch (level) {
    case 0: return [{ name: 'tent', part: 'tent', fit: 2.0, x: 0, z: -0.25, facing: 0 }];
    case 1: return [{ name: 'main', part: A, fit: 2.2, x: 0, z: -0.25, facing: 0 }];
    case 2: return [{ name: 'main', part: B, fit: 2.5, x: 0, z: -0.25, facing: 0 }];
    case 3: return [{ name: 'main', part: B, fit: 2.3, x: 0.2, z: -0.15, facing: 0 }, back];
    case 4: return [{ name: 'main', part: B, fit: 2.2, x: 0, z: -0.1, facing: 0 }, back,
      { name: 'sideRoom', part: A, fit: 1.3, x: 0.85, z: -0.35, facing: 3 }];
  }
}
/** The highest vertex of o's meshes, in `frame`'s coordinates (look-demo `topPoint`): the chimney top on the KayKit cottages.
 *  `frame` must be o or one of its ancestors. */
export function topPoint(o: THREE.Object3D, frame: THREE.Object3D = o): THREE.Vector3 {
  frame.updateMatrixWorld(true);
  const inv = frame.matrixWorld.clone().invert(), best = new THREE.Vector3(0, -Infinity, 0), v = new THREE.Vector3();
  o.traverse((c) => {
    const m = c as THREE.Mesh;
    if (!m.isMesh || m.name === 'windowPane') return;
    const pos = m.geometry.attributes.position;
    if (!pos) return;
    for (let i = 0; i < pos.count; i++) { v.fromBufferAttribute(pos, i).applyMatrix4(m.matrixWorld).applyMatrix4(inv); if (v.y > best.y) best.copy(v); }
  });
  return best;
}
export const builder: CodeBuilder = {
  id: 'house',
  async build(p, { assets }) {
    const level = Math.max(0, Math.min(4, Math.round(Number(p.level ?? 0)))) as HouseLevel;
    const id = String(p.catalogId ?? 'w.house');
    const root = new THREE.Group();
    for (const h of houseParts(level, roofOf(p.roof))) {
      const piece = await assets.part(id, h.part);
      const size = (piece.userData.size as THREE.Vector3 | undefined) ?? new THREE.Box3().setFromObject(piece).getSize(new THREE.Vector3());
      piece.scale.multiplyScalar(h.fit / Math.max(size.x, size.z, 1e-6));
      piece.rotation.y = -h.facing * (Math.PI / 2);
      piece.position.set(h.x, 0, h.z);
      piece.name = h.name;
      root.add(piece);
    }
    if (level > 0) root.userData.chimney = topPoint(root.getObjectByName('main')!, root).add(new THREE.Vector3(0, 0.1, 0));   // smoke FX
    if (p.building === true) addScaffold(root);                         // sized to the kit pieces, so before the garden
    if (p.resident === true) addFrontGarden(root, String(p.friendDye ?? ''));
    return root;
  },
};

// ---------- M1-T14: construction scaffold and friends' front garden (presentation colours, DESIGN.md "Cottage")
const FENCE_WHITE = '#FFFFFF', SOIL = '#8A6A4F', STEM = '#4A9152', POLE = '#B08A63', PLANK = '#C9A274', SIGN = '#F6C85F', NO_DYE_BLOOM = '#F4A6C0';
const POLE_R = 0.04;
const EDGE = 1.5 - POLE_R - 0.01;   // pole centres stay this far in, so the poles stay inside the 3 × 3 footprint (±1.5)
/** The flower bed's blooms: the owner's friend dye twice, with white and marigold between (Contract change 35: the friend colour on the plot). */
export function bloomColours(friendDye: string): string[] { const d = friendDye || NO_DYE_BLOOM; return [d, '#FFFFFF', d, '#F6C85F']; }
const shaded = (m: THREE.Mesh) => { m.castShadow = true; m.receiveShadow = true; return m; };

/** Four timber poles at the composite's corners and two planks across its front, clamped inside the footprint, plus a small sign. */
export function addScaffold(house: THREE.Group): void {
  const box = new THREE.Box3().setFromObject(house);
  const x0 = Math.max(-EDGE, box.min.x - 0.08), x1 = Math.min(EDGE, box.max.x + 0.08);
  const z0 = Math.max(-EDGE, box.min.z - 0.08), z1 = Math.min(GARDEN_Z + 0.1, box.max.z + 0.08), h = box.max.y + 0.1;
  const poles: THREE.BufferGeometry[] = [], planks: THREE.BufferGeometry[] = [];
  for (const [x, z] of [[x0, z0], [x1, z0], [x0, z1], [x1, z1]] as const) poles.push(new THREE.CylinderGeometry(POLE_R, POLE_R, h, 8).translate(x, h / 2, z));
  for (const y of [h * 0.4, h * 0.85]) planks.push(new RoundedBoxGeometry(x1 - x0, 0.06, 0.14, 2, 0.02).translate((x0 + x1) / 2, y, z1));
  const g = new THREE.Group(); g.name = 'scaffold';
  g.add(shaded(new THREE.Mesh(mergeGeometries(poles), toyMaterial(POLE))), shaded(new THREE.Mesh(mergeGeometries(planks), toyMaterial(PLANK))));
  const sign = shaded(new THREE.Mesh(new RoundedBoxGeometry(0.6, 0.22, 0.05, 2, 0.03), toyMaterial(SIGN)));
  sign.position.set(Math.min(x1 - 0.35, 0.6), 0.45, z1 + 0.04);
  g.add(sign);
  house.add(g);
}

/** A low white picket fence along the front edge (a gap at the door) and a flower bed in the front-left corner, both in the garden
 *  strip in front of the kit pieces. The fence is one mesh; the bed is one merged mesh per colour on the shared toy materials (soil,
 *  stems, then each bloom colour), because the outdoor static batch keeps only position, normal and uv, so vertex colours would not
 *  survive it, and the batch folds same-material meshes across every house. */
export function addFrontGarden(house: THREE.Group, friendDye: string): void {
  const pickets: THREE.BufferGeometry[] = [];
  for (const side of [-1, 1]) {
    for (let i = 0; i < 4; i++) {                                         // 4 pickets each side of a 0.9-wide door gap
      const x = side * (0.55 + i * 0.26);
      pickets.push(new RoundedBoxGeometry(0.08, 0.34, 0.05, 2, 0.02).translate(x, 0.17, 1.42));
    }
    pickets.push(new RoundedBoxGeometry(0.9, 0.05, 0.04, 2, 0.015).translate(side * 0.94, 0.24, 1.40));   // rail
  }
  const fence = shaded(new THREE.Mesh(mergeGeometries(pickets), toyMaterial(FENCE_WHITE))); fence.name = 'picketFence';
  const byColour = new Map<string, THREE.BufferGeometry[]>();
  const put = (hex: string, geo: THREE.BufferGeometry) => { const k = hex.toUpperCase(); byColour.set(k, [...(byColour.get(k) ?? []), geo]); };
  const blooms = bloomColours(friendDye);
  put(SOIL, new RoundedBoxGeometry(0.62, 0.08, 0.28, 2, 0.03).translate(-1.05, 0.04, 1.2));
  blooms.forEach((hex, i) => {
    const x = -1.28 + i * 0.15;
    put(STEM, new THREE.CylinderGeometry(0.014, 0.014, 0.18, 5).translate(x, 0.17, 1.2));
    put(hex, new THREE.SphereGeometry(0.068, 10, 8).scale(1, 0.7, 1).translate(x, 0.28, 1.2));
  });
  const bed = new THREE.Group(); bed.name = 'flowerBed'; bed.userData.blooms = blooms;
  for (const [hex, geos] of byColour) bed.add(shaded(new THREE.Mesh(mergeGeometries(geos.map((g) => (g.index ? g.toNonIndexed() : g))), toyMaterial(hex))));
  house.add(fence, bed);
}
